import { useState, useMemo, useEffect } from "react";
import { Plus, Search } from "lucide-react";
import { ProductTable } from "../../../components/products/ProductsTable";
import { ProductForm } from "./ProductForm";
import { DeleteConfirmModal } from "../../../components/categories/DeleteConfirmModal";
import { ProductsSidebar } from "./ProductsSidebar";
import { useProducts } from "../../../context/ProductsContext";
import { Product } from "../../../types/product";
import { useAuth } from "../../../auth/AuthContext";
import { useToast } from "../../../hooks/useToast";
import {
  createAdminProduct,
  getAdminProducts,
  ProductMutationPayload,
  updateAdminProduct,
  storeProductImages,
  updateProductImages,
  deleteAdminProduct,
} from "../../../services/productsApi";
import PageHeader from "../../../components/ui/PageHeader";
import { Package } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

const ProductsPage = () => {
  const { products, setProducts } = useProducts();
  const { user } = useAuth();

  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const toast = useToast();

  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(true);

  // 🟣 Add product handler
  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowForm(true);
  };

  // 🟣 Edit product handler
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  // 🟣 Delete confirmation handler
  const handleDeleteProduct = (id: string) => {
    const product = products.find((p) => p.id === id) || null;
    setDeletingProduct(product);
  };

  // Filter products
  const filteredProducts = useMemo(() => {
    return products.filter((product) =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.barcode && product.barcode.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [products, searchTerm]);

  // Paginated products
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const data = await getAdminProducts();
        setProducts(data);
      } catch (error) {
        console.error("Failed to fetch products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [setProducts]);

  // 🟣 Save (Add/Edit) product handler
  const toNumber = (...values: Array<string | number | undefined | null>) => {
    for (const value of values) {
      if (typeof value === "number" && Number.isFinite(value)) return value;
      if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return undefined;
  };

  const handleSaveProduct = async (
    productData: Omit<Product, "id"> & { id?: string }
  ) => {
    const companyId =
      toNumber(user?.company_id, user?.company?.id, user?.companyId) ?? 1;
    const locationId =
      toNumber(
        user?.location_id,
        user?.location?.id,
        user?.locationId,
        user?.default_location_id
      ) ?? 1;

    // Build payload - map form fields to API payload
    const payload: ProductMutationPayload = {
      company_id: companyId,
      location_id: locationId,
      name: productData.name.trim(),
      price: productData.price,
    };

    // Add discount_type if discount exists
    if ((productData as any).discountType) {
      payload.discount_type = (productData as any).discountType;
    } else if ((productData as any).discount !== undefined && (productData as any).discount > 0) {
      // Default to percent if discount exists but no type specified
      payload.discount_type = "percent";
    }

    // Add optional fields if they exist in productData
    if ((productData as any).description && (productData as any).description.trim() !== "") {
      payload.description = (productData as any).description.trim();
    }
    if ((productData as any).barcode && (productData as any).barcode.trim() !== "") {
      payload.barcode = (productData as any).barcode.trim();
    }
    if ((productData as any).sku && (productData as any).sku.trim() !== "") {
      payload.sku = (productData as any).sku.trim();
    }
    if ((productData as any).discount !== undefined && (productData as any).discount > 0) {
      payload.discount = (productData as any).discount;
    }
    if ((productData as any).taxIds && Array.isArray((productData as any).taxIds) && (productData as any).taxIds.length > 0) {
      payload.tax_ids = (productData as any).taxIds;
    }

    setSavingProduct(true);
    try {
      let productId: string;

      // First, create or update the product (without images)
      if (productData.id) {
        await updateAdminProduct(productData.id, payload);
        productId = productData.id;
      } else {
        const created = await createAdminProduct(payload);
        productId = created.id;
      }

      // Handle images
      const imageFiles = (productData as any).imageFiles || [];
      const existingImageUrls = (productData as any).existingImageUrls || [];
      const defaultImage = (productData as any).defaultImage;

      if (productData.id) {
        // Update existing product - always preserve existing images unless explicitly removed
        // Extract filenames from existing image URLs (these are the images to keep)
        const existingImageFilenames = existingImageUrls
          .map((url: string) => {
            if (typeof url !== "string" || !url) return "";

            // Remove query parameters and hash if any
            const urlWithoutQuery = url.split('?')[0].split('#')[0];

            // Handle blob URLs (preview URLs) - skip these as they're not saved images
            if (urlWithoutQuery.startsWith('blob:')) {
              return "";
            }

            // Extract filename from URL
            const parts = urlWithoutQuery.split('/');
            let filename = parts[parts.length - 1];

            // If no filename found or it's a placeholder, try to extract from path
            if (!filename || filename.trim() === "" || filename === "product-0" || filename === "product-1") {
              // Look backwards through path segments for a valid filename
              for (let i = parts.length - 1; i >= 0; i--) {
                const segment = parts[i];
                if (segment && segment.includes('.') && !segment.startsWith('product-')) {
                  filename = segment;
                  break;
                }
              }
            }

            return filename;
          })
          .filter((filename: string) => {
            // Filter out empty strings and placeholder names
            return filename.trim() !== "" &&
              filename !== "product-0" &&
              filename !== "product-1" &&
              !filename.startsWith('blob:');
          });

        // Always call updateProductImages to ensure images are preserved
        // If no new files and no existing files, it will clear all (user removed all)
        // If existing files, it will keep them
        const imageUpdateResult = await updateProductImages(
          productId,
          imageFiles,
          existingImageFilenames,
          defaultImage
        );

        // If we got updated images from the API, use them to update the product
        if (imageUpdateResult && imageUpdateResult.images) {
          // Update the product in the list with new images
          setProducts((prevProducts) =>
            prevProducts.map((p) =>
              p.id === productId
                ? {
                  ...p,
                  image: imageUpdateResult.images[0],
                  images: imageUpdateResult.images,
                }
                : p
            )
          );

          // If we're editing this product, update the editingProduct as well
          if (editingProduct && editingProduct.id === productId) {
            setEditingProduct({
              ...editingProduct,
              image: imageUpdateResult.images[0],
              images: imageUpdateResult.images,
            } as Product);
          }
        }
      } else {
        // New product - upload images
        if (imageFiles.length > 0) {
          await storeProductImages(
            productId,
            imageFiles,
            defaultImage
          );
        }
      }

      // Refresh the products list to get updated images
      try {
        const latest = await getAdminProducts();
        if (latest.length > 0) {
          setProducts(latest);
          // If editing, update the editingProduct with fresh data
          if (productData.id) {
            const updatedProduct = latest.find((p) => p.id === productData.id);
            if (updatedProduct) {
              setEditingProduct(updatedProduct);
            }
          }
        }
      } catch (refreshError) {
        console.error("Failed to refresh products after save", refreshError);
      }

      setShowForm(false);
      setEditingProduct(null);
      // Show success message
      const message = productData.id
        ? "Product updated successfully!"
        : "Product created successfully!";
      toast.success(message);
    } catch (error: any) {
      console.error("Failed to save product", error);

      let msg = error.response?.data?.message || "Could not save the product. Please try again.";

      // Special handling for json_decode error which happens even on success
      if (msg.includes("json_decode") || msg.includes("must be of type string, array given")) {
        // Treat as success
        setShowForm(false);
        setEditingProduct(null);

        // Refresh list
        try {
          const latest = await getAdminProducts();
          if (latest.length > 0) {
            setProducts(latest);
          }
        } catch (refreshError) {
          console.error("Failed to refresh products after partial success", refreshError);
        }

        const message = productData.id ? "Product updated successfully!" : "Product created successfully!";
        toast.success(message);
        return;
      }

      // Handle validation errors specifically
      if (error.response?.data?.errors) {
        const validationErrors = Object.values(error.response.data.errors).flat();
        if (validationErrors.length > 0) {
          // Join multiple errors with a newline or just show the first few
          msg = validationErrors.join("\n");
        }
      }

      toast.error(msg);
    } finally {
      setSavingProduct(false);
    }
  };

  // 🟣 Cancel form
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  // 🟣 Row click → open sidebar
  const handleRowClick = (product: Product) => {
    setSelectedProduct(product);
    setSidebarOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col w-full h-full">
          {/* Header */}
          <PageHeader
            title="Products"
            description="Manage your salon product inventory and information"
            icon={Package}
            variant="card"
            className="mb-0"
            mobileActionsInHeader={true}
            actions={
              <button
                onClick={handleAddProduct}
                className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
              >
                <Plus className="w-3.5 h-3.5 lg:w-5 lg:h-5" />
                <span>Add Product</span>
              </button>
            }
          />

          {/* Toolbar */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
            {/* Left: Per Page Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">Show</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[80px] bg-white border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">per page</span>
            </div>

            {/* Right: Search */}
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 pb-6">
            <ProductTable
              products={paginatedProducts}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              onRowClick={handleRowClick}
              isLoading={isLoading}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 mt-4 border-t border-gray-200 bg-white rounded-b-xl">
                <div className="text-sm text-gray-600 text-center sm:text-left">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Product Form Modal */}
        {showForm && (
          <ProductForm
            product={editingProduct}
            onSave={handleSaveProduct}
            onCancel={handleCancelForm}
            isSubmitting={savingProduct}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deletingProduct}
        type="product"
        name={deletingProduct?.name || ""}
        onClose={() => setDeletingProduct(null)}
        onConfirm={async () => {
          if (deletingProduct) {
            try {
              await deleteAdminProduct(deletingProduct.id);
              // Remove from local state
              setProducts(products.filter((p) => p.id !== deletingProduct.id));
              setDeletingProduct(null);
              // Show success message
              toast.success("Product deleted successfully!");
            } catch (error: any) {
              console.error("Failed to delete product:", error);
              toast.error(error.response?.data?.message || "Failed to delete product. Please try again.");
            }
          }
        }}
      />

      {/* Sidebar */}
      <ProductsSidebar
        isOpen={sidebarOpen}
        product={selectedProduct}
        onClose={() => setSidebarOpen(false)}
        onEdit={handleEditProduct}
      />
    </div>
  );
};

export default ProductsPage;
